import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { apiService } from "lib/apiService";

const dashStyles = ["Solid"];

const monthLabels = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

export default function CategoryWiseExpenseChart({ years, selectedGlobalYear, currency }) {
    const history = useHistory();
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [selectedYear, setSelectedYear] = useState(selectedGlobalYear || new Date().getFullYear());
    const [chartSeries, setChartSeries] = useState([]);

    useEffect(() => {
        setSelectedYear(selectedGlobalYear); // Sync with global year when it changes
    }, [selectedGlobalYear]);

    useEffect(() => {
        // Check if user is not logged in (localStorage is empty)
        if (!localStorage.getItem("userData")) {
            // Redirect to the login page
            history.push("/login");
        }
    }, [history]);

    //get userid form loca storage
    const storedUserData = localStorage.getItem("userData");
    let userId = "";
    let groupId = "";
    if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        userId = userData.userId;
        groupId = userData.groupId;
    }

    // Detect dark mode
    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDarkMode(document.documentElement.classList.contains("dark"));
        });
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["class"],
        });

        setIsDarkMode(document.documentElement.classList.contains("dark"));

        return () => observer.disconnect();
    }, []);

    // Fetch category-wise monthly chart data
    useEffect(() => {
        const fetchData = async () => {
            if (!groupId) return;

            try {
                const data = await apiService.getCategoryExpenses(groupId, selectedYear);

                const categories = [
                    ...new Set(
                        data.map((d) => d.expenseDescType.trim()).filter((name) => name !== "")
                    ),
                ];

                const seriesData = categories.map((category, index) => {
                    const monthlyData = new Array(12).fill(null);

                    data.forEach((entry) => {
                        if (entry.expenseDescType.trim() === category) {
                            const monthIndex = parseInt(entry.month, 10) - 1;
                            monthlyData[monthIndex] = entry.totalExpenses;
                        }
                    });

                    return {
                        name: category || "Unknown",
                        data: monthlyData,
                        dashStyle: dashStyles[index % dashStyles.length],
                    };
                });

                setChartSeries(seriesData);
            } catch (error) {
                console.error("Error loading category-wise expenses:", error);
            }
        };

        fetchData();
    }, [selectedYear, groupId]);

    // Highcharts Config
    const options = {
        accessibility: { enabled: false },
        chart: { type: "spline", backgroundColor: "transparent" },
        title: {
            text: null,
            style: { color: isDarkMode ? "#f9fafb" : "#1f2937" },
        },
        xAxis: {
            categories: monthLabels,
            title: { text: "Month", style: { color: isDarkMode ? "#d1d5db" : "#4b5563" } },
            labels: { style: { color: isDarkMode ? "#d1d5db" : "#4b5563" } },
            gridLineColor: isDarkMode ? "#374151" : "#e5e7eb",
        },
        yAxis: {
            title: {
                text: `Total Expenses (${currency})`,
                style: { color: isDarkMode ? "#d1d5db" : "#4b5563" },
            },
            labels: { style: { color: isDarkMode ? "#d1d5db" : "#4b5563" } },
            gridLineColor: isDarkMode ? "#374151" : "#e5e7eb",
        },
        legend: {
            align: "center",
            verticalAlign: "bottom",
            layout: "horizontal",
            backgroundColor: isDarkMode ? "#111827" : "#ffffff",
            itemStyle: {
                color: isDarkMode ? "#f9fafb" : "#1f2937",
                fontWeight: "500",
                fontSize: "14px",
            },
            borderColor: isDarkMode ? "#374151" : "#e5e7eb",
            symbolWidth: 40,
        },
        tooltip: {
            shared: true,
            valuePrefix: currency,
            stickOnContact: true,
            backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
            style: { color: isDarkMode ? "#f9fafb" : "#1f2937" },
        },
        plotOptions: {
            series: {
                connectNulls: true,
                marker: { enabled: true, radius: 4 },
                cursor: "pointer",
                lineWidth: 2,
            },
        },
        series: chartSeries,
        credits: { enabled: false },
    };

    return (
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-4 w-full">
            {/* Header */}
            <div className="flex flex-col lg:flex-row justify-between mb-3">
                <h2 className="text-lg font-bold text-gray-800 dark:text-white">
                    Category-wise Monthly Expenses
                </h2>

                <select
                    className="text-gray-800 dark:text-white rounded-md px-3 py-2 text-sm"
                    style={{ minWidth: "80px", border: "solid #ddd 1px" }}
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                    {years.map((year) => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>
            </div>

            {/* Chart */}
            <div className="w-full">
                <HighchartsReact highcharts={Highcharts} options={options} />
            </div>
        </div>
    );
}
